import styles from './CreateChatMemberForm.module.scss';
import { useUserContextSelector } from '@app/main/utils';
import { Icon } from '@app/ui';
import { Avatar, chatifyApi, CreateChatMemberInputProps } from '@webinex/chatify';
import { Checkbox, Form, Input, List } from 'antd';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChatAllowedMemberIds } from './useChatAllowedMemberIds';

interface CheckboxListProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  searchValue: string | null;
  allowedIds: string[] | undefined;
  isAllowedIdsLoading: boolean;
}

function CheckboxList(props: CheckboxListProps) {
  const { value, onChange, searchValue, allowedIds, isAllowedIdsLoading } = props;
  const userId = useUserContextSelector((x) => x.id);

  // Pass ids to the server so only authorized accounts are fetched.
  // Skip while allowedIds is still resolving so we never request the unfiltered list.
  const { data: accounts, isFetching } = chatifyApi.useGetAccountListQuery(
    { ids: allowedIds },
    { skip: allowedIds === undefined && isAllowedIdsLoading },
  );

  const dataSource = useMemo(
    () =>
      accounts?.filter(
        (x) =>
          x.id !== userId &&
          (!searchValue || x.name.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())),
      ) ?? [],
    [accounts, userId, searchValue],
  );

  return (
    <List
      rowKey="id"
      className="wxchtf-create-chat-member-input-list"
      loading={isFetching || isAllowedIdsLoading}
      dataSource={dataSource}
      renderItem={(x) => {
        const checked = value?.includes(x.id);

        return (
          <List.Item className={checked ? '--selected' : ''}>
            <Checkbox
              checked={checked}
              onChange={() =>
                onChange &&
                onChange(value?.includes(x.id) ? value!.filter((y) => y !== x.id) : [...value!, x.id])
              }
            >
              <List.Item.Meta avatar={<Avatar account={x} />} title={x.name} />
            </Checkbox>
          </List.Item>
        );
      }}
    />
  );
}

export const CreateChatMemberForm = (props: CreateChatMemberInputProps) => {
  const { name } = props;
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState<string | null>(null);
  const { allowedIds, preSelectedIds, hideMemberList, isLoading } = useChatAllowedMemberIds();
  const form = Form.useFormInstance();

  useEffect(() => {
    if (hideMemberList && preSelectedIds) {
      form.setFieldValue(name, preSelectedIds);
    }
  }, [hideMemberList, preSelectedIds, form, name]);

  if (hideMemberList) {
    return (
      <Form.Item name={name} hidden noStyle>
        <span />
      </Form.Item>
    );
  }

  return (
    <>
      <span className={styles.label}>{t('chats.create.members.label')}</span>
      <div className={styles.searchBox}>
        <Input
          className={styles.search}
          value={searchValue ?? ''}
          placeholder={t('search')}
          addonBefore={<Icon type="search" />}
          onChange={(e) => setSearchValue(isEmpty(e.target.value) ? null : e.target.value)}
        />
      </div>
      <Form.Item rules={[{ required: true, message: t('chats.create.members.help') }]} name={name}>
        <CheckboxList
          searchValue={searchValue}
          allowedIds={allowedIds}
          isAllowedIdsLoading={isLoading}
        />
      </Form.Item>
    </>
  );
};
